import type { AppUser } from '@/domain/user'
import type { AuthProvider } from './AuthProvider'
import type { UserRepository } from '@/repositories/UserRepository'

const SESSION_KEY = 'cybertales.devSession.user'

// Development-only implementation of AuthProvider. Lets us switch between
// the six demo roles without a real backend (Document 11 §4/§9). This class
// is dev-mode-only; it is never wired into a production build — see
// src/config/services.ts, the single place that decides which AuthProvider
// implementation is active.
export class DevAuthProvider implements AuthProvider {
  private listeners = new Set<(user: AppUser | null) => void>()

  constructor(private users: UserRepository) {}

  getCurrentUser(): AppUser | null {
    try {
      const raw = localStorage.getItem(SESSION_KEY)
      return raw ? (JSON.parse(raw) as AppUser) : null
    } catch {
      return null
    }
  }

  async login(userId: string): Promise<AppUser> {
    const user = await this.users.getById(userId)
    if (!user) throw new Error(`Unknown demo user: ${userId}`)
    localStorage.setItem(SESSION_KEY, JSON.stringify(user))
    this.notify(user)
    return user
  }

  async logout(): Promise<void> {
    localStorage.removeItem(SESSION_KEY)
    this.notify(null)
  }

  subscribe(listener: (user: AppUser | null) => void): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  private notify(user: AppUser | null) {
    this.listeners.forEach((l) => l(user))
  }
}
